import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Actions,
  getOneMonthFinancialStats,
} from "../../../actions/admin.action";
import Alert from "../../../components/alert";
import { OneMonthFinancialStats } from "../../../dto";
import { ReactComponent as Loader } from "../../../images/loader.svg";
import { ForbiddenError } from "../../../middleware";
import { ReduxState } from "../../../reducers";
import { isLoadingActive } from "../../../utils";
import InfoCardList from "../infoCardList";
import { ReportSectionAuthStatus } from "../types";
import { fetchDataForSelectedMonth, getOneMonthLoanSection } from "../utils";

interface OneMonthFinancialStatsProps {
  selectedDate: Date | null;
  onAuthorizationChange: ReportSectionAuthStatus;
}

export default function OneMonthLoanStats({
  selectedDate,
  onAuthorizationChange,
}: OneMonthFinancialStatsProps) {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.admin.loadingQueue
  );
  const oneMonthFinancialStats: OneMonthFinancialStats | null = useSelector(
    (state: ReduxState) => state.admin.oneMonthFinancialStats
  );

  const [errorMessage, setErrorMessage] = useState("");
  const [notAuthorized, setNotAuthorized] = useState(false);

  const isLoading = isLoadingActive(loadingQueue, [
    Actions.REQUEST_GET_ONE_MONTH_FINANCIAL_STATS,
  ]);

  useEffect(() => {
    if (!selectedDate) return;
    fetchDataForSelectedMonth(
      getOneMonthFinancialStats,
      storeDispatch,
      selectedDate
    )
      .then(() => onAuthorizationChange("oneMonth", "authorized"))
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setNotAuthorized(true);
          onAuthorizationChange("oneMonth", "forbidden");
        } else {
          onAuthorizationChange("oneMonth", "error");
        }
      });
  }, [selectedDate, storeDispatch, onAuthorizationChange]);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center dashboard-container p-3">
        <Loader />
      </div>
    );
  }

  if (notAuthorized)
    return (
      <div className="dashboard-container">
        <Alert
          type="danger"
          message="Not authorized to view 1 month loan stats."
        />
      </div>
    );

  if (!oneMonthFinancialStats)
    return (
      <div className="d-flex align-items-baseline justify-content-center">
        <p className="text-muted">
          Unable to load summary stats. Please reload the page again.
        </p>
      </div>
    );

  const oneMonthLoanSection = getOneMonthLoanSection(oneMonthFinancialStats);
  const { infoCards, sectionName } = oneMonthLoanSection;

  return (
    <div className="dashboard-container">
      <Alert
        message={errorMessage}
        type="info"
        canDismiss
        onDismissClick={() => setErrorMessage("")}
      />

      <section>
        <h3 className="user-page-title">{sectionName}</h3>
        <InfoCardList infoCards={infoCards} />
      </section>
    </div>
  );
}
